import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2, Globe, Shield, AlertTriangle } from 'lucide-react';

export default function Settings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [envVars, setEnvVars] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [has404, setHas404] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (id) loadSettings(); }, [id]);

  async function loadSettings() {
    try {
      const [proj, env, nf] = await Promise.all([
        api.getProject(id!), api.getEnvVars(id!), api.get404Status(id!)
      ]);
      setProject(proj.project);
      setName(proj.project.name);
      setSubdomain(proj.project.subdomain);
      setEnvVars(env.env || {});
      setHas404(nf.has_custom_404);
    } catch (e: any) { toast.error(e.message); } finally { setLoading(false); }
  }

  async function saveGeneral() {
    setSaving(true);
    try {
      await api.updateProject(id!, { name, subdomain });
      toast.success('Settings saved');
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  async function addEnvVar() {
    if (!newKey || !newValue) return;
    const updated = { ...envVars, [newKey]: newValue };
    try {
      await api.setEnvVars(id!, updated);
      setEnvVars(updated);
      setNewKey(''); setNewValue('');
      toast.success('Env var added');
    } catch (e: any) { toast.error(e.message); }
  }

  async function removeEnvVar(key: string) {
    try {
      await api.deleteEnvVar(id!, key);
      const updated = { ...envVars }; delete updated[key];
      setEnvVars(updated);
      toast.success('Removed');
    } catch (e: any) { toast.error(e.message); }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${project?.name}" forever? This cannot be undone.`)) return;
    try {
      await api.deleteProject(id!);
      toast.success('Project deleted');
      navigate('/dashboard');
    } catch (e: any) { toast.error(e.message); }
  }

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-accent" size={24} /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Link to={`/project/${id}`} className="flex items-center gap-2 text-text-muted hover:text-text-primary mb-6"><ArrowLeft size={16} /> Back</Link>
      <h1 className="text-xl font-bold mb-8">Project Settings</h1>

      {/* General */}
      <section className="card mb-6">
        <h2 className="font-semibold text-sm mb-4 flex items-center gap-2"><Globe size={14} className="text-accent" /> General</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-text-muted mb-1">Project Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs text-text-muted mb-1">Subdomain</label>
            <div className="flex items-center gap-0">
              <input value={subdomain} onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} className="input text-sm rounded-r-none" />
              <span className="bg-surface border border-l-0 border-border rounded-r-lg px-3 py-2 text-text-muted text-xs">.meganhost.dpdns.org</span>
            </div>
          </div>
          <button onClick={saveGeneral} disabled={saving} className="btn-primary text-xs">{saving ? <Loader2 size={14} className="animate-spin inline" /> : 'Save Changes'}</button>
        </div>
      </section>

      {/* Environment Variables */}
      <section className="card mb-6">
        <h2 className="font-semibold text-sm mb-4 flex items-center gap-2"><Shield size={14} className="text-accent-blue" /> Environment Variables</h2>
        <p className="text-xs text-text-muted mb-4">Access these in your site as <code className="bg-elevated px-1 rounded text-accent">window.__ENV__</code></p>
        {Object.keys(envVars).length > 0 && (
          <div className="mb-4 space-y-2">
            {Object.entries(envVars).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 bg-elevated rounded-lg px-3 py-2 group">
                <code className="text-accent text-xs font-mono flex-1">{k}</code>
                <code className="text-text-muted text-xs">{v.length > 25 ? v.slice(0,25)+'...' : v}</code>
                <button onClick={() => removeEnvVar(k)} className="p-1 text-text-muted hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="KEY" className="input text-xs flex-1 font-mono" />
          <input value={newValue} onChange={e => setNewValue(e.target.value)} placeholder="value" className="input text-xs flex-1" />
          <button onClick={addEnvVar} className="btn-secondary text-xs flex items-center gap-1 shrink-0"><Plus size={14} /> Add</button>
        </div>
      </section>

      {/* Danger */}
      <section className="card border-danger/20">
        <h2 className="font-semibold text-sm mb-3 flex items-center gap-2 text-danger"><AlertTriangle size={14} /> Danger Zone</h2>
        <p className="text-xs text-text-muted mb-3">Permanently delete this project and all files. No undo.</p>
        <button onClick={handleDelete} className="btn-danger text-xs">Delete Project</button>
      </section>
    </div>
  );
}
